/**
 * useCastChange - who a cut brought on, took off and carried over, on this screen (plan design-gaps, Z3; the scenes
 * page: travellers pulse where they land, the cast stands up with a ring, leavers fade).
 *
 * A screen hears a cut's tokens before its stage document (useScenes writes the stage last), so it keeps, per figure,
 * when it first drew it and when it last did. When the cut is heard (its stamp changes) the ones that came and went
 * in the moments before - and after, for a token that comes in late - are the cut's (engine/scenes/castChange); the
 * change plays when the display's set rises (DisplayScene's `rise`, a count), which may be much later, behind a
 * curtain the GM holds down. The figures this screen loaded with are not news. Travellers are the stage's own list, those this screen draws. The change lasts CAST_MS, then
 * the figures simply stand; a screen that holds still gets none.
 *
 * @param {readonly { id: string }[]} tokens - the figures this screen draws
 * @param {number|null} cutAt - the stage's cut stamp
 * @param {number} rise - the display's rises played (0: none yet)
 * @param {readonly string[]|undefined} travellers - the stage's travellers, by id
 * @param {boolean} still - the viewer's motion setting
 * @returns {{ entering: string[], leavers: object[], travellers: object[] }} the figures by id, the leavers' last
 *   tokens and the travellers' tokens
 */
import { useEffect, useRef, useState } from 'react';
import { castChange } from '../engine/scenes/castChange';

/** How long the cut's cast change plays: the longest of its marks, the leavers' fade. */
export const CAST_MS = 1000;
const NO_CAST = Object.freeze({ entering: [], leavers: [], travellers: [] });
/** How long a figure gone is remembered: a curtain the GM holds down may hold the rise back minutes. */
const KEEP_GONE_MS = 10 * 60 * 1000;

export default function useCastChange(tokens, cutAt, rise, travellers, still) {
  const seen = useSeenAndGone(tokens);
  const heard = useHeardAt(cutAt);
  const [cast, setCast] = useState(NO_CAST);
  // Only a new rise plays the change: the travellers and the motion setting are read as they stand at it.
  const at = useRef({ travellers, still });
  at.current = { travellers, still };
  useEffect(() => {
    if (!rise || at.current.still || !seen.current) return undefined;
    const { first, gone, last } = seen.current;
    const { entering, leavers } = castChange(first, gone, heard.current);
    const carried = (at.current.travellers ?? []).map((id) => last.get(id)).filter(Boolean);
    setCast({ entering, leavers, travellers: carried });
    const t = setTimeout(() => setCast(NO_CAST), CAST_MS);
    return () => clearTimeout(t);
  }, [rise, seen, heard]);
  return still ? NO_CAST : cast;
}

/** When this screen heard the stage's latest cut stamp, ms. */
function useHeardAt(cutAt) {
  const heard = useRef(0);
  useEffect(() => {
    heard.current = Date.now();
  }, [cutAt]);
  return heard;
}

/**
 * When this screen first drew each figure (those it loaded with at 0: never news), its last token, and the figures
 * it stopped drawing with when - those older than the lookback forgotten, so the record stays small.
 */
function useSeenAndGone(tokens) {
  const seen = useRef(null);
  useEffect(() => {
    const last = new Map(tokens.map((t) => [t.id, t]));
    if (seen.current) noteDrawn(seen.current, last, Date.now());
    else seen.current = { first: new Map(tokens.map((t) => [t.id, 0])), last, gone: new Map() };
  }, [tokens]);
  return seen;
}

/** The record moved on to what the screen draws now: the newcomers first seen, the missing gone. */
function noteDrawn(was, last, now) {
  for (const id of last.keys()) if (!was.first.has(id)) was.first.set(id, now);
  noteGone(was, last, now);
  was.last = last;
}

/** The figures no longer drawn: gone now, and new again should they come back; the long gone forgotten. */
function noteGone(was, last, now) {
  for (const [id, token] of was.last) {
    if (last.has(id)) continue;
    was.first.delete(id);
    was.gone.set(id, { token, at: now });
  }
  for (const [id, g] of was.gone) if (g.at < now - KEEP_GONE_MS || last.has(id)) was.gone.delete(id);
}
